import { Document, BLOCKS } from '@contentful/rich-text-types';
import { isVisibleToCurrentBrand } from '@/lib/utils/visibility';
import {
  ContentfulEntry,
  ContentfulLinkFields,
  ContentfulMenuFields,
  NavigationItem,
  NavigationLink,
  NavigationMenu,
  isContentfulEntry,
  isContentfulAsset,
  ContentfulNavigationGridFields,
  ContentfulTextPairsFields,
  ContentfulAsset,
  ContentfulBrandIntegrations,
  ContentfulBrandSettingsFields,
  LocationPageFields,
  ContentfulSys,
} from '@/types/contentful';
import { TrustWidgetEntryFields } from '@/components/TrustWidget/types';
import { LargeImageHeroBannerFields } from '@/components/LargeImageHeroBanner/types';
import { OneColumnLayoutFields } from '@/components/OneColumnLayout/types';
import { ContentfulFrontendComponentFields } from '@/components/FrontendComponent/types';
import {
  OneColumnBackgroundColor,
  OneColumnHorizontalAlignment,
  OneColumnVerticalAlignment,
  OneColumnStyle,
} from '@/components/OneColumnLayout/types';
import {
  TwoColumnBackgroundColor,
  TwoColumnLayoutFields,
  TwoColumnHorizontalAlignment,
  TwoColumnVerticalAlignment,
  TwoColumnStyle,
} from '@/components/TwoColumnLayout/types';
import {
  ThreeColumnBackgroundColor,
  ThreeColumnLayoutFields,
  ThreeColumnHorizontalAlignment,
  ThreeColumnVerticalAlignment,
  ThreeColumnStyle,
} from '@/components/ThreeColumnLayout/types';
import { HeadingFields } from '@/components/Heading/types';
import {
  ContentfulAccordionFields,
  ContentfulAccordionItemEntry,
} from '@/components/Accordion/types';
import { ContentfulOfferCardFields } from '@/components/OfferCard/types';
import { OffersFields } from '@/components/Offers/types';
import { ContentfulMediaFields, MediaAssetData } from '@/components/Media/types';
import { ContentfulArticleEntry, PageArticleFields } from '@/components/Article/types';
import { ContentfulFeaturedArticlesFields } from '@/components/FeaturedArticles/types';
import type { ContentfulOverlineFields, OverlineProps } from '@/components/Overline/types';
import { ContentfulRichTextFields } from '@/components/RichText/types';
import { MediumImageHeroBannerFields } from '@/components/MediumImageHeroBanner/types';
import { PLPHeroBannerFields } from '@/components/PLPHeroBanner/types';
import { TextOnlyHeroFields } from '@/components/TextOnlyHero/types';
import { LEARNING_HUB_BASE_PATH } from '@/lib/constants';
import { ContentfulGridTileEntry, ContentfulGridTilesFields } from '@/components/GridTiles/types';
import { ContentfulGridMediaFields } from '@/components/GridMedia/types';
import type {
  ContentfulIconTileEntry,
  ContentfulIconTileGroupFields,
} from '@/components/IconTileGroup/types';
import type { LocationHeroFields } from '@/components/LocationHero/types';
import type {
  LocationTwoColumnTextFields,
  LocationFieldsFields,
} from '@/components/LocationTwoColumnText/types';
import { isSvgFile, resolveUrl } from '@/lib/utils/contentful';

export function transformOneColumnLayout(fields: OneColumnLayoutFields) {
  return {
    __typename: 'OneColumnLayout' as const,
    components: Array.isArray(fields.content) ? fields.content : [],
    backgroundColor: (fields.backgroundColor as OneColumnBackgroundColor) || undefined,
    isFullWidth: Boolean(fields.isFullWidth) || false,
    density: Boolean(fields.density) || false,
    horizontalAlignment: (fields.horizontalAlignment as OneColumnHorizontalAlignment) || undefined,
    verticalAlignment: (fields.verticalAlignment as OneColumnVerticalAlignment) || undefined,
    style: (fields.style as OneColumnStyle) || undefined,
    anchorId: fields.anchorId,
  };
}

export function transformHeading(fields: HeadingFields) {
  return {
    __typename: 'Heading' as const,
    anchorId: fields.anchorId ?? undefined,
    text: fields.text ?? '',
    headingType: fields.type || undefined,
    alignment: (fields.alignment as string) || undefined,
    backgroundColor: (fields.backgroundColor as string) || undefined,
    textColor: (fields.textColor as string) || undefined,
  };
}

function transformAccordionItem(item: ContentfulAccordionItemEntry | null | undefined) {
  return {
    title: item?.fields?.title ?? '',
    copy: item?.fields?.copy ?? null,
  };
}

export function transformAccordion(fields: ContentfulAccordionFields) {
  return {
    __typename: 'Accordion' as const,
    anchorId: fields.anchorId ?? undefined,
    items: Array.isArray(fields.accordionItems)
      ? fields.accordionItems
          // Filter out nested items restricted to other brands.
          .filter(
            (item): item is NonNullable<typeof item> =>
              item != null && isVisibleToCurrentBrand(item as unknown as ContentfulEntry),
          )
          .map(transformAccordionItem)
      : [],
    showNumbers: Boolean(fields.showNumbers),
  };
}

export function transformOfferCard(fields: ContentfulOfferCardFields) {
  return {
    __typename: 'OfferCard' as const,
    title: (fields.title as string) || '',
    teaser: fields.teaser ?? null,
    details: fields.details ?? null,
    linkTitle: fields.linkTitle || 'Offer Details',
    brandList: fields.brandList ?? [],
    expirationDate: fields.expirationDate ?? null,

    alternativeTitle: fields.alternativeTitle ?? null,
    alternativeTeaser: fields.alternativeTeaser ?? null,
    availabilityBadge: fields.availabilityBadge ?? null,
  };
}

export function transformOffers(fields: OffersFields) {
  const leftOfferEntry = Array.isArray(fields.leftOffer) ? fields.leftOffer[0] : fields.leftOffer;
  const rightOfferEntry = Array.isArray(fields.rightOffer)
    ? fields.rightOffer[0]
    : fields.rightOffer;

  const visibleLeftOfferEntry =
    leftOfferEntry && isVisibleToCurrentBrand(leftOfferEntry) ? leftOfferEntry : null;

  const visibleRightOfferEntry =
    rightOfferEntry && isVisibleToCurrentBrand(rightOfferEntry) ? rightOfferEntry : null;

  const multipleOffers = Array.isArray(fields.multipleOffers)
    ? fields.multipleOffers.filter(
        // Guard against unresolved Contentful link stubs (no .fields) that can
        // appear when the entry tree exceeds the API's include depth.
        // Without this check, transformOfferCard(o.fields) throws
        // "Cannot read properties of undefined (reading 'title')" at build time.
        (offer) => !!offer?.fields && isVisibleToCurrentBrand(offer),
      )
    : [];

  const linkEntry = Array.isArray(fields.link) ? fields.link[0] : fields.link;

  return {
    __typename: 'Offers' as const,
    // Use title (new visible heading field) if set.
    // for backward compatibility with existing entries.
    name: fields.title || '',
    multipleOffers: multipleOffers.length
      ? multipleOffers.map((o) => {
          return transformOfferCard(o.fields as ContentfulOfferCardFields);
        })
      : null,
    leftOffer: visibleLeftOfferEntry?.fields
      ? transformOfferCard(visibleLeftOfferEntry.fields as ContentfulOfferCardFields)
      : null,
    rightOffer: visibleRightOfferEntry?.fields
      ? transformOfferCard(visibleRightOfferEntry.fields as ContentfulOfferCardFields)
      : null,
    link: linkEntry?.fields ? transformLink(linkEntry.fields as ContentfulLinkFields) : null,
  };
}

export const transformOverline = (fields: ContentfulOverlineFields): OverlineProps => {
  return {
    __typename: 'Overline',
    text: fields.overline,
  };
};
export function transformRichText(fields: ContentfulRichTextFields) {
  return {
    __typename: 'RichText' as const,
    anchorId: fields.anchorId ?? undefined,
    name: fields.name,
    content: fields.text ?? null,
  };
}

export function transformMediaAlignment(
  alignment: 'Left' | 'Center' | 'Right' = 'Left',
): 'justify-start' | 'justify-center' | 'justify-end' {
  if (alignment === 'Center') {
    return 'justify-center';
  } else if (alignment === 'Right') {
    return 'justify-end';
  } else {
    return 'justify-start';
  }
}

export function transformMediaAsset(
  asset: ContentfulAsset | null | undefined,
  customAltText?: string,
): MediaAssetData | undefined {
  if (!isContentfulAsset(asset)) return undefined;

  const file = asset.fields?.file;
  const imageDetails = file?.details?.image;

  const title = asset.fields?.title ?? '';
  const description = asset.fields?.description ?? '';
  const finalAltText = customAltText || description || title || '';

  return {
    id: asset.sys?.id ?? '',
    src: resolveUrl(file?.url),
    title,
    description,
    alt: finalAltText,
    fileName: file?.fileName ?? '',
    contentType: file?.contentType ?? '',
    isSvg: isSvgFile(file?.fileName, file?.contentType),
    width: imageDetails?.width,
    height: imageDetails?.height,
  };
}

/**
 * Resolves a content-image slot to its own raw asset, or to the brand's
 * default image when the slot is empty — operates on the raw ContentfulAsset,
 * before any shape conversion, so callers that pass the asset straight
 * through to a component (rather than via transformMediaAsset) don't need
 * any changes downstream. Only use this for content-image slots, never
 * icon/logo slots — those stay fallback-free.
 */
export function resolveAssetWithFallback(
  asset: ContentfulAsset | null | undefined,
  defaultBrandImage: ContentfulAsset | null | undefined,
): ContentfulAsset | undefined {
  if (isContentfulAsset(asset)) return asset;
  return isContentfulAsset(defaultBrandImage) ? defaultBrandImage : undefined;
}

export function transformMedia(
  fields: ContentfulMediaFields,
  defaultBrandImage?: ContentfulAsset | null,
) {
  const video = transformMediaAsset(fields.video);
  // Only fall back the image when there's no video either — a video-only
  // block must not have its video hidden behind the brand default image.
  const image = transformMediaAsset(
    resolveAssetWithFallback(fields.image, video ? undefined : defaultBrandImage),
    fields.imageAltText as string | undefined,
  );
  const alignment = transformMediaAlignment(fields.alignment);
  const roundedCorner = fields.roundedCorner ? 'rounded-2xl overflow-hidden' : '';

  const mediaType = image ? 'Image' : video ? 'Video' : null;
  const anchorId = fields.anchorId ?? undefined;
  return {
    __typename: 'Media' as const,
    mediaType,
    image,
    anchorId,
    video,
    alignment,
    roundedCorner,
  };
}

export function transformTrustWidget(fields: TrustWidgetEntryFields) {
  return {
    __typename: 'TrustWidget' as const,
    title: (fields.title as string) || '',
    trustWidgetItems: Array.isArray(fields.trustWidgetItems)
      ? fields.trustWidgetItems
          // Filter out nested items restricted to other brands.
          .filter(
            (item) => item != null && isVisibleToCurrentBrand(item as unknown as ContentfulEntry),
          )
          .map((item) => ({
            __typename: 'TrustWidgetItem' as const,
            title: (item?.fields?.title as string) || '',
            icon: transformMediaAsset(item?.fields?.icon),
          }))
      : [],
  };
}

export function transformLargeImageHeroBanner(
  fields: LargeImageHeroBannerFields,
  defaultBrandImage?: ContentfulAsset | null,
) {
  const video = isContentfulAsset(fields.video) ? fields.video : undefined;
  return {
    __typename: 'LargeImageHeroBanner' as const,
    anchorId: fields.anchorId ?? undefined,
    title: (fields.title as string) || '',
    // Only fall back the image when there's no video either — a video-only
    // banner must not have its video hidden behind the brand default image.
    image: resolveAssetWithFallback(fields.image, video ? undefined : defaultBrandImage),
    video,
    ctas: fields.link?.fields ? transformLink(fields.link.fields) : undefined,
    secondaryCtas: fields.secondaryLink?.fields
      ? transformLink(fields.secondaryLink.fields)
      : undefined,
    stats: Array.isArray(fields.stats)
      ? fields.stats.map((item) => item?.fields?.text).filter(Boolean)
      : [],
  };
}

export function transformMediumImageHeroBanner(
  fields: MediumImageHeroBannerFields,
  defaultBrandImage?: ContentfulAsset | null,
) {
  const eyebrowEntry = fields.eyebrow;

  const eyebrow = isContentfulEntry(eyebrowEntry)
    ? {
        label:
          (eyebrowEntry.fields?.label as string) || (eyebrowEntry.fields?.title as string) || '',
        href:
          (eyebrowEntry.fields?.url as string) ||
          (eyebrowEntry.fields?.freeformUrl as string) ||
          '',
      }
    : undefined;

  const primaryCta = fields.primaryCta ? transformLink(fields.primaryCta.fields) : undefined;
  const secondaryCta = fields.secondaryCta ? transformLink(fields.secondaryCta.fields) : undefined;

  return {
    __typename: 'MediumImageHeroBanner' as const,
    anchorId: fields.anchorId,
    title: (fields.title as string) || '',
    copy: fields.copy ?? null,
    image: resolveAssetWithFallback(fields.image, defaultBrandImage),
    eyebrow,
    primaryCta,
    secondaryCta,
  };
}

export function transformPLPHeroBanner(fields: PLPHeroBannerFields) {
  const offerFields = fields.offer?.fields;

  // Extract only the fields consumed by usePLPHeroBannerViewModel instead of
  // passing the raw Contentful entry. The raw entry contains offer.brandList
  // which links to the full brandSettings entry (including mainNavigation and
  // its entire menu tree), creating a 16+ level circular reference chain that
  // causes SSG prerendering to crash with "Cannot read properties of undefined".
  //
  // brandList is simplified to string[] of brand codes, which is the format
  // already supported by isVisibleToCurrentBrand (see visibility.ts).
  const offer = offerFields
    ? {
        fields: {
          title: offerFields.title ?? null,
          alternativeTitle: offerFields.alternativeTitle ?? null,
          availabilityBadge: offerFields.availabilityBadge ?? null,
          teaser: offerFields.teaser ?? null,
          alternativeTeaser: offerFields.alternativeTeaser ?? null,
          details: offerFields.details ?? null,
          linkTitle: offerFields.linkTitle ?? null,
          expirationDate: offerFields.expirationDate ?? null,
          brandList: Array.isArray(offerFields.brandList)
            ? offerFields.brandList
                .map((brand) => {
                  if (typeof brand === 'string') return brand;
                  // Full brandSettings entry → extract only the brand code string
                  const entry = brand as { fields?: { brandCode?: string } };
                  return entry?.fields?.brandCode ?? null;
                })
                .filter((code): code is string => code !== null)
            : [],
        },
      }
    : null;

  return {
    __typename: 'PLPHeroBanner' as const,
    anchorId: fields.anchorId ?? undefined,
    title: (fields.title as string) || '',
    copy: fields.copy ?? null,
    offer,
    layout: fields.layout ?? 'default',
    primaryCtaLabelOverride: fields.primaryCtaLabelOverride ?? undefined,
    secondaryCtaLabelOverride: fields.secondaryCtaLabelOverride ?? undefined,
    iconTileGroup: fields.iconTileGroup?.fields
      ? transformIconTileGroup(fields.iconTileGroup.fields)
      : null,
  };
}

export function transformTextOnlyHero(fields: TextOnlyHeroFields) {
  return {
    __typename: 'TextOnlyHero' as const,
    anchorId: fields.anchorId ?? undefined,
    heroTitle: fields.heroTitle ?? null,
    heroCopy: fields.copy ?? null,
    eyebrow:
      fields.eyebrow && isContentfulEntry(fields.eyebrow)
        ? transformLink(fields.eyebrow.fields as ContentfulLinkFields)
        : undefined,
    showMiniReviews: Boolean(fields.showMiniReviews),
  };
}

export function transformLink(fields: ContentfulLinkFields, phoneNumber?: string): NavigationLink {
  // Defensive fallback to prevent SSR prerender TypeError if Contentful returns an unresolved/empty link.
  const entry = fields || {};

  const url = entry.url?.trim() || '';
  return {
    __typename: 'Link' as const,
    id: '', // populated by transformMenu when walking the entry tree
    label: entry.label || entry.title || '',
    title: entry.title,
    url,
    targetAttribute: entry.target === 'Force New Tab' ? '_blank' : '_self',
    relAttribute: entry.relAttribute ?? '',
    icon: transformMediaAsset(entry.icon),
    priority: entry.priority,
    resolvedPhoneNumber: entry.priority === 'CallRail' ? phoneNumber : undefined,
    resolvedTelHref:
      entry.priority === 'CallRail' && phoneNumber
        ? `tel:${phoneNumber.replace(/\D/g, '')}`
        : undefined,
  };
}

export function transformFeaturedArticles(
  fields: {
    header?: string;
    articles?: ContentfulArticleEntry[];
    relatedArticlesType?: 'Category' | 'MostRecent';
    articleCategories?: ContentfulEntry[];
  },
  defaultBrandImage?: ContentfulAsset | null,
) {
  const { header = '', articles, relatedArticlesType, articleCategories } = fields || {};

  return {
    __typename: 'FeaturedArticles' as const,
    header,
    relatedArticlesType,
    articleCategories,
    articles: Array.isArray(articles)
      ? articles
          .filter((articleEntry) => {
            // Filter out articles without fields or not visible to current brand
            if (!articleEntry || !articleEntry.fields) return false;
            return isVisibleToCurrentBrand(articleEntry);
          })
          .map((articleEntry) => {
            const card = transformArticleCard(articleEntry, defaultBrandImage);
            return card;
          })
          .filter((a: unknown) => a !== null)
      : [],
  };
}

export function transformArticle(
  fields: PageArticleFields,
  sys: ContentfulSys,
  defaultBrandImage?: ContentfulAsset | null,
) {
  // Filter related articles by brand visibility
  const filteredRelatedArticles = fields.relatedArticles
    ? fields.relatedArticles.filter((article) => isVisibleToCurrentBrand(article))
    : undefined;

  return {
    __typename: 'PageArticle' as const,
    title: fields.title || '',
    slug: fields.slug || '',
    metaTitle: fields.metaTitle || undefined,
    metaDescription: fields.metaDescription || undefined,
    brandList: fields.brandList,
    articleContent: fields.articleContent,
    // No video-priority guard needed here: ArticleHero.tsx's `heroVideo ??
    // heroImage` already prioritizes video whenever present, regardless of
    // what heroImage resolves to.
    heroImage: resolveAssetWithFallback(fields.heroImage, defaultBrandImage),
    heroVideo: isContentfulAsset(fields.heroVideo) ? fields.heroVideo : undefined,
    relatedArticles: filteredRelatedArticles,
    articleCategories: fields.articleCategories,
    relatedArticlesType: fields.relatedArticlesType,
    articleId: sys.id,
    createdAt: sys.createdAt,
    updatedAt: sys.updatedAt,
  };
}

export function transformTextPairs(fields: ContentfulTextPairsFields) {
  return {
    __typename: 'TextPairs' as const,
    title: fields.title || '',
    copy: fields.copy,
  };
}

export function transformNavigationGrid(fields: ContentfulNavigationGridFields) {
  const rawItems = Array.isArray(fields.items) ? fields.items : [];

  const visibleItems = rawItems.filter(isContentfulEntry).filter(isVisibleToCurrentBrand);

  return {
    __typename: 'NavigationGrid' as const,
    anchorId: fields.anchorId ?? undefined,
    name: fields.name || '',
    items: visibleItems.map((entry) => transformLink(entry.fields as ContentfulLinkFields)),
  };
}

export function transformFrontendComponent(fields: ContentfulFrontendComponentFields) {
  return {
    __typename: 'FrontendComponent' as const,
    anchorId: fields.anchorId,
    title: fields.title || '',
    componentType: fields.componentType || '',
    size: fields.sizeForSpacer?.replace(' ', '') || undefined,
    propertyForLocationSearch: fields.propertyForLocationSearch || undefined,
    reviewHeading: fields.reviewHeading || null,
    subheading: fields.subheading,
  };
}

export function transformArticleCard(
  article: ContentfulArticleEntry,
  defaultBrandImage?: ContentfulAsset | null,
) {
  const categoryEntry = article.fields.articleCategories?.[0];
  const categoryName =
    (categoryEntry?.fields as { name?: string; title?: string } | undefined)?.name ||
    (categoryEntry?.fields as { name?: string; title?: string } | undefined)?.title ||
    '';

  const categories = (article.fields.articleCategories || [])
    .map(
      (cat) =>
        (cat?.fields as { name?: string; title?: string } | undefined)?.name ||
        (cat?.fields as { name?: string; title?: string } | undefined)?.title ||
        '',
    )
    .filter(Boolean);

  return {
    __typename: 'ArticleCard' as const,
    sysId: article.sys.id,
    title: article.fields.title || '',
    image:
      resolveAssetWithFallback(
        article.fields.heroImage as unknown as ContentfulAsset,
        defaultBrandImage,
      ) ?? null,
    linkUrl: `${LEARNING_HUB_BASE_PATH}/${article.fields.slug}`,
    category: categoryName,
    categories,
  };
}

export function transformTwoColumnLayout(fields: TwoColumnLayoutFields) {
  return {
    __typename: 'TwoColumnLayout' as const,
    leftContent: Array.isArray(fields.leftContent) ? fields.leftContent : [],
    rightContent: Array.isArray(fields.rightContent) ? fields.rightContent : [],
    backgroundColor: (fields.backgroundColor as TwoColumnBackgroundColor) || undefined,
    isFullWidth: Boolean(fields.isFullWidth) || false,
    density: Boolean(fields.density) || false,
    horizontalAlignment: (fields.horizontalAlignment as TwoColumnHorizontalAlignment) || undefined,
    verticalAlignment: (fields.verticalAlignment as TwoColumnVerticalAlignment) || undefined,
    style: (fields.style as TwoColumnStyle) || undefined,
    anchorId: fields.anchorId,
  };
}

export function transformThreeColumnLayout(fields: ThreeColumnLayoutFields) {
  return {
    __typename: 'ThreeColumnLayout' as const,
    leftContent: Array.isArray(fields.leftContent) ? fields.leftContent : [],
    middleContent: Array.isArray(fields.middleContent) ? fields.middleContent : [],
    rightContent: Array.isArray(fields.rightContent) ? fields.rightContent : [],
    backgroundColor: (fields.backgroundColor as ThreeColumnBackgroundColor) || undefined,
    isFullWidth: Boolean(fields.isFullWidth) || false,
    density: Boolean(fields.density) || false,
    horizontalAlignment:
      (fields.horizontalAlignment as ThreeColumnHorizontalAlignment) || undefined,
    verticalAlignment: (fields.verticalAlignment as ThreeColumnVerticalAlignment) || undefined,
    style: (fields.style as ThreeColumnStyle) || undefined,
    anchorId: fields.anchorId,
  };
}

function transformGridTileItem(item: ContentfulGridTileEntry) {
  return {
    title: item?.fields?.title ?? '',
    copy: item?.fields?.copy ?? null,
  };
}

export function transformGridTiles(fields: ContentfulGridTilesFields) {
  return {
    __typename: 'GridTiles' as const,
    anchorId: fields.anchorId ?? undefined,
    title: fields.title || '',
    items: Array.isArray(fields.tiles)
      ? fields.tiles
          // Filter out nested items restricted to other brands.
          .filter(
            (item): item is NonNullable<typeof item> =>
              item != null && isVisibleToCurrentBrand(item as unknown as ContentfulEntry),
          )
          .map(transformGridTileItem)
      : [],
  };
}

export function transformGridMedia(fields: ContentfulGridMediaFields) {
  return {
    __typename: 'GridMedia' as const,
    anchorId: fields.anchorId ?? undefined,
    title: fields.title || '',
    items: Array.isArray(fields.mediaItems)
      ? fields.mediaItems.filter((item): item is ContentfulAsset => isContentfulAsset(item))
      : [],
  };
}

export function transformIconTileItem(item: ContentfulIconTileEntry) {
  return {
    id: item.sys?.id ?? '',
    label: item.fields?.tileLabel ?? '',
    icon: transformMediaAsset(item.fields?.tileIcon),
    anchorId: item.fields?.targetSection?.fields?.anchorId ?? '',
  };
}

export function transformIconTileGroup(fields: ContentfulIconTileGroupFields) {
  return {
    __typename: 'IconTileGroup' as const,
    heading: fields.heading ?? '',
    items: Array.isArray(fields.iconTiles)
      ? fields.iconTiles
          .filter((item): item is ContentfulIconTileEntry => Boolean(item?.fields))
          .map(transformIconTileItem)
      : [],
  };
}

export function transformLocationContactFooter(fields: Record<string, unknown>) {
  return {
    __typename: 'LocationContactFooter' as const,
    heading: fields.heading || undefined,
    leftColumn: fields.leftColumn || undefined,
    rightColumn: fields.rightColumn || undefined,
  };
}

export function transformEntry(
  content: ContentfulEntry | unknown,
  defaultBrandImage?: ContentfulAsset | null,
) {
  if (!isContentfulEntry(content)) return null;

  const sys = content.sys;
  const contentType = sys.contentType?.sys?.id;
  const fields = content.fields;

  if (!contentType) return null;
  switch (contentType) {
    case 'oneColumnLayout':
      return transformOneColumnLayout(fields as unknown as OneColumnLayoutFields);
    case 'twoColumnLayout':
      return transformTwoColumnLayout(fields as unknown as TwoColumnLayoutFields);
    case 'threeColumnLayout':
      return transformThreeColumnLayout(fields as unknown as ThreeColumnLayoutFields);
    case 'heading':
      return transformHeading(fields as unknown as HeadingFields);
    case 'accordion':
      return transformAccordion(fields as unknown as ContentfulAccordionFields);
    case 'offer':
      return transformOfferCard(fields as unknown as ContentfulOfferCardFields);
    case 'offers':
      return transformOffers(fields as unknown as OffersFields);
    case 'overline':
      return transformOverline(fields as unknown as ContentfulOverlineFields);
    case 'richText':
      return transformRichText(fields as unknown as ContentfulRichTextFields);
    case 'media':
      return transformMedia(fields as unknown as ContentfulMediaFields, defaultBrandImage);
    case 'largeImageHeroBanner':
      return transformLargeImageHeroBanner(
        fields as unknown as LargeImageHeroBannerFields,
        defaultBrandImage,
      );
    case 'trustWidget':
      return transformTrustWidget(fields as unknown as TrustWidgetEntryFields);
    case 'link':
      return transformLink(fields as unknown as ContentfulLinkFields);
    case 'menu':
      return transformMenu(content as unknown as ContentfulEntry<ContentfulMenuFields>);
    case 'featuredArticles':
      return transformFeaturedArticles(
        fields as unknown as ContentfulFeaturedArticlesFields,
        defaultBrandImage,
      );
    case 'article':
      return transformArticle(fields as unknown as PageArticleFields, sys, defaultBrandImage);
    case 'mediumImageHeroBanner':
      return transformMediumImageHeroBanner(
        fields as unknown as MediumImageHeroBannerFields,
        defaultBrandImage,
      );
    case 'plpHeroBanner':
      return transformPLPHeroBanner(fields as unknown as PLPHeroBannerFields);
    case 'textOnlyHero':
      return transformTextOnlyHero(fields as unknown as TextOnlyHeroFields);
    case 'navigationGrid':
      return transformNavigationGrid(fields as unknown as ContentfulNavigationGridFields);
    case 'textPairs':
      return transformTextPairs(fields as unknown as ContentfulTextPairsFields);
    case 'frontEndComponentWidget':
      return transformFrontendComponent(fields as unknown as ContentfulFrontendComponentFields);
    case 'gridTiles':
      return transformGridTiles(fields as unknown as ContentfulGridTilesFields);
    case 'gridMedia':
      return transformGridMedia(fields as unknown as ContentfulGridMediaFields);
    case 'locationPage':
      return transformLocationPage(fields as unknown as LocationPageFields);
    case 'locationHero':
      return transformLocationHero(fields as unknown as LocationHeroFields, defaultBrandImage);
    case 'locationTwoColumnText':
      return transformLocationTwoColumnText(fields as unknown as LocationTwoColumnTextFields);
    case 'locationContactFooter':
      return transformLocationContactFooter(fields);
    default:
      console.warn(`Unknown content type: ${contentType} `);
      return null;
  }
}

// ─── Navigation Transformer ───────────────────────────────────────────────────

function transformNavItem(
  entry: ContentfulEntry | null | undefined,
  ancestors: Set<string>,
  phoneNumber?: string,
): NavigationItem | null {
  if (!isContentfulEntry(entry)) return null;

  const id = entry.sys?.id ?? '';

  // True circular reference — this exact entry is already an ancestor on the
  // current path. Stop here to avoid infinite recursion.
  if (id && ancestors.has(id)) return null;

  const contentTypeId = entry.sys?.contentType?.sys?.id;

  if (contentTypeId === 'link') {
    const link = transformLink(entry.fields as unknown as ContentfulLinkFields, phoneNumber);
    return { ...link, id };
  }

  if (contentTypeId === 'menu') {
    return transformMenu(
      entry as unknown as ContentfulEntry<ContentfulMenuFields>,
      ancestors,
      phoneNumber,
    );
  }

  return null;
}

export function transformMenu(
  entry: ContentfulEntry<ContentfulMenuFields> | null | undefined,
  ancestors: Set<string> = new Set(),
  phoneNumber?: string,
): NavigationMenu | null {
  if (!isContentfulEntry(entry)) return null;

  const id = entry.sys?.id ?? '';

  // Already in the current ancestor path → circular reference, stop.
  if (id && ancestors.has(id)) return null;

  // Add this menu's ID to the current path before descending.
  if (id) ancestors.add(id);

  const fields = entry.fields as ContentfulMenuFields;
  const rawItems = Array.isArray(fields.menuItems) ? fields.menuItems : [];

  const items: NavigationItem[] = rawItems
    .map((item) => transformNavItem(item as ContentfulEntry | null, ancestors, phoneNumber))
    .filter((item): item is NavigationItem => item !== null);

  // Remove this menu's ID after descending so sibling branches
  // (or other trees) are not incorrectly blocked.
  if (id) ancestors.delete(id);

  return {
    __typename: 'NavigationMenu' as const,
    id,
    title: fields.title ?? '',
    menuName: fields.menuName ?? null,
    items,
  };
}

export function transformNavigation(
  brandSettings: ContentfulEntry<ContentfulBrandSettingsFields>,
  brandIntegrations?: ContentfulEntry<ContentfulBrandIntegrations> | null,
): {
  mainNav: NavigationMenu | null;
  footerNav: NavigationMenu | null;
  logo: ReturnType<typeof transformMediaAsset>;
} {
  const mainNavEntry = brandSettings.fields?.mainNavigation;
  const footerNavEntry = brandSettings.fields?.footerNavigation;
  const callRailPhoneNumber = brandIntegrations?.fields?.callRailPhoneNumber as string | undefined;

  // Separate ancestor Sets — mainNav and footerNav are independent trees.
  const mainNav = transformMenu(mainNavEntry, new Set(), callRailPhoneNumber);
  const footerNav = transformMenu(footerNavEntry, new Set(), callRailPhoneNumber);

  if (mainNav) {
    // Find both the booking (Primary) and phone (CallRail) CTAs at the root level.
    const primaryCta = mainNav.items.find(
      (item) => item.__typename === 'Link' && item.priority === 'Primary',
    ) as NavigationLink | undefined;
    const callRailCta = mainNav.items.find(
      (item) => item.__typename === 'Link' && item.priority === 'CallRail',
    ) as NavigationLink | undefined;

    if (primaryCta) mainNav.primaryCtaLink = primaryCta;
    if (callRailCta) mainNav.callRailCtaLink = callRailCta;
  }

  const logo = transformMediaAsset(brandSettings.fields?.logo);

  return { mainNav, footerNav, logo };
}

export function collectAllNavLinks(items: NavigationItem[]): NavigationLink[] {
  const links: NavigationLink[] = [];

  function walk(navItems: NavigationItem[]): void {
    for (const item of navItems) {
      if (item.__typename === 'Link') {
        links.push(item);
      } else if (item.__typename === 'NavigationMenu') {
        // menu — recurse into its children
        walk(item.items);
      }
    }
  }

  walk(items);
  return links;
}

export function transformLocationHero(
  fields: LocationHeroFields & {
    injectedTitle?: Document;
    injectedCopy?: Document;
  },
  defaultBrandImage?: ContentfulAsset | null,
) {
  const transformedImage = transformMediaAsset(
    resolveAssetWithFallback(fields.image, defaultBrandImage),
  );
  return {
    __typename: 'LocationHero' as const,
    name: fields.name ?? undefined,
    eyebrow:
      fields.eyebrow && isContentfulEntry(fields.eyebrow)
        ? transformLink(fields.eyebrow.fields as unknown as ContentfulLinkFields)
        : undefined,
    primaryCta:
      fields.primaryCta && isContentfulEntry(fields.primaryCta)
        ? transformLink(fields.primaryCta.fields as unknown as ContentfulLinkFields)
        : undefined,

    heroImage: transformedImage
      ? {
          src: transformedImage.src,
          alt: transformedImage.alt ?? '',
          width: transformedImage.width ?? 0,
          height: transformedImage.height ?? 0,
        }
      : undefined,
    // Injected at runtime from locationFields:
    injectedTitle: fields.injectedTitle ?? undefined,
    injectedCopy: fields.injectedCopy ?? undefined,
  };
}

/**
 * Constructs the LocationHero component data by merging the base Contentful entry
 * with the dynamically injected title and copy from the globally shared locationFields.
 */
export function assembleLocationHeroData(
  heroEntry: ContentfulEntry,
  locationFields: ReturnType<typeof transformLocationFields> | null,
  defaultBrandImage?: ContentfulAsset | null,
) {
  const baseData = transformLocationHero(
    heroEntry.fields as unknown as LocationHeroFields,
    defaultBrandImage,
  );
  return {
    ...baseData,
    injectedTitle: locationFields?.heroLocationTitle,
    injectedCopy: locationFields?.heroLocationCopy,
  };
}

export function transformLocationTwoColumnText(
  fields: LocationTwoColumnTextFields & {
    leftTitle?: Document;
    rightCopy?: Document;
    leftContent?: Document;
    rightIntro?: Document;
    rightEnding?: Document;
    officeLink?: { href?: string; label?: string };
  },
) {
  return {
    __typename: 'LocationTwoColumnText' as const,
    icon: isContentfulAsset(fields.icon) ? fields.icon : undefined,
    // Injected at runtime from locationFields:
    leftTitle: fields.leftTitle ?? undefined,
    rightCopy: fields.rightCopy ?? undefined,
    // Injected at runtime from locationPage:
    leftContent: fields.leftContent ?? undefined,
    rightIntro: fields.rightIntro ?? undefined,
    rightEnding: fields.rightEnding ?? undefined,
    officeLink: fields.officeLink ?? undefined,
  };
}

/**
 * Helper: Converts a plain string to a minimal Contentful RichText Document
 * with a single paragraph node.
 */
export function stringToDoc(text?: string): Document | undefined {
  if (!text?.trim()) return undefined;
  return {
    nodeType: 'document',
    data: {},
    content: [
      {
        nodeType: 'paragraph',
        data: {},
        content: [{ nodeType: 'text', value: text, marks: [], data: {} }],
      },
    ],
  } as Document;
}

/**
 * Helper: Builds the left-column RichText Document by combining the raw
 * string address with the existing Contentful schedule Document.
 */
export function mergeAddressAndSchedule(
  address?: string,
  schedule?: Document,
): Document | undefined {
  const content: Document['content'] = [];
  if (address) {
    address.split('\n').forEach((line) => {
      if (line.trim()) {
        content.push({
          nodeType: BLOCKS.PARAGRAPH,
          data: {},
          content: [{ nodeType: 'text', value: line.trim(), marks: [], data: {} }],
        });
      }
    });
  }
  if (schedule?.nodeType === 'document') {
    content.push(...schedule.content);
  }
  if (!content.length) return undefined;
  return { nodeType: 'document', data: {}, content } as Document;
}

/**
 * Constructs the LocationTwoColumnText component data by merging the base Contentful entry
 * with both the global template copy (locationFields) and the branch-specific data (locationPage).
 */
export function assembleLocationTwoColData(
  twoColEntry: ContentfulEntry,
  locationFields: ReturnType<typeof transformLocationFields> | null,
  locationPage: ReturnType<typeof transformLocationPage>,
  dynamicCallRailPhone?: { label?: string; href?: string },
  dynamicAddress?: string,
) {
  const baseData = transformLocationTwoColumnText(
    twoColEntry.fields as unknown as LocationTwoColumnTextFields,
  );
  return {
    ...baseData,
    leftTitle: locationFields?.twoColumnTextLeftTitle,
    rightCopy: locationFields?.twoColumnTextRightCopy,
    leftContent: mergeAddressAndSchedule(
      dynamicAddress,
      locationPage.schedule as Document | undefined,
    ),
    rightIntro: stringToDoc(locationPage.localizedIntro),
    rightEnding: stringToDoc(locationPage.endingCopy),
    callRailPhone: dynamicCallRailPhone,
  };
}

export function transformLocationFields(fields: LocationFieldsFields) {
  return {
    metaTitle: fields.metaTitle ?? undefined,
    metaDescription: fields.metaDescription ?? undefined,
    twoColumnTextLeftTitle: fields.twoColumnTextLeftTitle ?? undefined,
    twoColumnTextRightCopy: fields.twoColumnTextRightCopy ?? undefined,
    heroLocationTitle: fields.heroLocationTitle ?? undefined,
    heroLocationCopy: fields.heroLocationCopy ?? undefined,
  };
}

// ─── LocationPage Transformer ─────────────────────

export function transformLocationPage(fields: LocationPageFields) {
  return {
    __typename: 'LocationPage' as const,
    name: fields.name,
    slug: fields.slug,
    schedule: fields.schedule,
    localizedIntro: fields.localizedIntro,
    endingCopy: fields.endingCopy,
  };
}
